import { startSubmit, stopSubmit } from 'redux-form'
import { call, put, takeEvery } from 'redux-saga/effects'
import {
	appCreateRequest,
	appCreateSuccess,
	appCreateFailure,
	appDeleteRequest,
	appDeleteSuccess,
	appDeleteFailure,
	appUpdateRequest,
	appUpdateSuccess,
	appUpdateFailure,
	appInstallRequest,
	appInstallSuccess,
	appInstallFailure,
	appUninstallRequest,
	appUninstallSuccess,
	appUninstallFailure,
	appListRequest,
	appListSuccess,
	appListFailure,
	installedAppListRequest,
	installedAppListSuccess,
	installedAppListFailure,
	appPublishRequest,
	appPublishSuccess,
	appPublishFailure
} from '../actions'

import { apiRequest, waitForState } from 'utils'

import history from '../history'

function* requestCreateApp({ payload: { name, type, formName, handleClose } }) {
	try {
		yield put(startSubmit(formName))

		const token = yield call(waitForState, state => state.user.token)
		const body = {
			payload: {
				name,
				description: '',
				clientId: type ? type : 'chatio',
				redirectUri: 'https://app.chat.io/',
				customData: {
					name,
					placement: 'sidebar'
				}
			}
		}

		const response = yield call(() => {
			return apiRequest(
				token,
				'/application',
				'post',
				JSON.stringify(body)
			)
		})

		const json = yield call(() => response.json())

		switch (response.status) {
			case 202: {
				yield put(stopSubmit(formName))
				yield call(() => handleClose())

				const newAppPayload = {
					id: json.id,
					name: body.payload.name,
					ssoSecret: json.secret,
					clientId: body.payload.clientId,
					receivedAt: Date.now()
				}
				const appSlug = json.id ? json.id : json

				yield history.push(
					'/apps/' +
						appSlug +
						(body.payload.clientId === 'chatio'
							? '/display'
							: '/authorization')
				)

				yield put(appCreateSuccess(newAppPayload))
				break
			}
			case 400: {
				throw new Error(
					"Bummer! It seems your account doesn't have enough permissions to create an app. Please contact admin or owner of your chat.io license."
				)
			}
			default: {
				throw new Error("App couldn't be created: " + response.error)
			}
		}
	} catch (error) {
		yield put(stopSubmit(formName, { appName: error.message }))
		yield put(appCreateFailure({ error }))
	}
}

function* requestUpdateApp({ payload: { id, formName, ...rest } }) {
	try {
		yield put(startSubmit(formName))

		const token = yield call(waitForState, state => state.user.token)
		const response = yield call(() => {
			const body = {
				payload: {
					...rest
				}
			}
			return apiRequest(
				token,
				'/application/' + id,
				'post',
				JSON.stringify(body)
			)
		})

		if (response.status === 202) {
			const updatedAppPayload = {
				app: {
					id,
					...rest
				},
				receivedAt: Date.now()
			}
			yield put(appUpdateSuccess(updatedAppPayload))
			yield put(stopSubmit(formName))
		} else {
			throw new Error('Something went wrong. Please try again.')
		}
	} catch (error) {
		yield put(stopSubmit(formName, { _error: error.message }))
		yield put(appUpdateFailure({ error }))
	}
}

function* requestDeleteApp({ payload: { id, formName, ...rest } }) {
	try {
		yield put(startSubmit(formName))
		const token = yield call(waitForState, state => state.user.token)

		const response = yield call(() => {
			return apiRequest(
				token,
				'/application/' + id + '/deactivate',
				'post'
			)
		})

		if (response.status === 202) {
			// const json = yield call(() => response.json())
			const deletedAppPayload = {
				id,
				receivedAt: Date.now()
			}
			yield put(stopSubmit(formName))
			yield history.push('/apps/')
			yield put(appDeleteSuccess(deletedAppPayload))
		} else {
			throw new Error(
				"App couldn't be deleted. It is probably submitted to review, published or accepted and thus cannot be removed."
			)
		}
	} catch (error) {
		yield put(stopSubmit(formName, { delete: error.message }))
		yield put(appDeleteFailure({ error }))
	}
}

// totally worth refactoring
function* requestInstallApp({
	payload: { id, formName, appInstalledForTeam, ...rest }
}) {
	try {
		yield put(startSubmit(formName))
		const token = yield call(waitForState, state => state.user.token)

		const response = yield call(() => {
			return apiRequest(token, '/install/' + id, 'post')
		})

		if (response.status === 202) {
			// const json = yield call(() => response.json())
			const installedAppPayload = {
				id,
				receivedAt: Date.now()
			}
			yield put(appInstallSuccess(installedAppPayload))
			yield put(stopSubmit(formName))
		} else {
			throw new Error("App couldn't be installed")
		}
	} catch (error) {
		yield put(stopSubmit(formName, error))
		yield put(appInstallFailure({ error }))
	}
}

function* requestPublishApp({ payload: { id, formName, ...rest } }) {
	try {
		yield put(startSubmit(formName))
		const token = yield call(waitForState, state => state.user.token)

		const response = yield call(() => {
			return apiRequest(token, '/application/' + id + '/publish', 'post')
		})

		if (response.status === 202) {
			// const json = yield call(() => response.json())
			const publishedAppPayload = {
				id,
				receivedAt: Date.now()
			}
			yield put(appPublishSuccess(publishedAppPayload))
			yield put(stopSubmit(formName))
		} else {
			throw new Error("App couldn't be published")
		}
	} catch (error) {
		yield put(stopSubmit(formName, error))
		yield put(appPublishFailure({ error }))
	}
}

function* requestUninstallApp({
	payload: { id, formName, appInstalledForTeam, ...rest }
}) {
	try {
		yield put(startSubmit(formName))
		const token = yield call(waitForState, state => state.user.token)

		const response = yield call(() => {
			return apiRequest(token, '/uninstall/' + id, 'post')
		})

		if (response.status === 202) {
			// const json = yield call(() => response.json())
			const uninstalledAppPayload = {
				id,
				receivedAt: Date.now()
			}
			yield put(appUninstallSuccess(uninstalledAppPayload))
			yield put(stopSubmit(formName))
		} else {
			throw new Error("App couldn't be installed")
		}
	} catch (error) {
		yield put(stopSubmit(formName, error))
		yield put(appUninstallFailure({ error }))
	}
}

function* requestAppList({ payload }) {
	try {
		const token = yield call(waitForState, state => state.user.token)
		const response = yield call(() => {
			return apiRequest(token, '/developer/apps', 'get')
		})

		if (response.status === 200) {
			const json = yield call(() => response.json())
			const payload = yield {
				items: json.reduce((prev, next, i) => {
					prev[next.id] = next
					return prev
				}, {}),
				receivedAt: Date.now()
			}
			yield put(appListSuccess(payload))
		} else {
			throw new Error("Apps list couldn't be loaded")
		}
	} catch (error) {
		yield put(appListFailure({ error }))
	}
}

function* requestInstalledAppList({ payload }) {
	try {
		const token = yield call(waitForState, state => state.user.token)
		const response = yield call(() => {
			return apiRequest(token, '/license/apps', 'get')
		})

		if (response.status === 200) {
			const json = yield call(() => response.json())
			const payload = yield {
				items: json.map(item => {
					return item.id
				}),
				receivedAt: Date.now()
			}
			yield put(installedAppListSuccess(payload))
		} else {
			throw new Error("Installed apps list couldn't be loaded")
		}
	} catch (error) {
		yield put(installedAppListFailure({ error }))
	}
}

export function* watchAppRequests() {
	yield takeEvery(appCreateRequest, requestCreateApp)
	yield takeEvery(appDeleteRequest, requestDeleteApp)
	yield takeEvery(appUpdateRequest, requestUpdateApp)
	yield takeEvery(appInstallRequest, requestInstallApp)
	yield takeEvery(appUninstallRequest, requestUninstallApp)
	yield takeEvery(appPublishRequest, requestPublishApp)
	yield takeEvery(appUpdateSuccess, requestAppList)
	yield takeEvery(appPublishSuccess, requestAppList)
	yield takeEvery(appListRequest, requestAppList)
	yield takeEvery(installedAppListRequest, requestInstalledAppList)
}



// WEBPACK FOOTER //
// ./src/sagas/apps.js